import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { functions } from '../utils/firebase';
import { formatPrice } from '../types';
import type { PublicService, CustomerInfo, DepositSettings } from '../types';
import { DepositCheckout } from '../components/DepositCheckout';
import { BookingStepper } from '../components/BookingStepper';

interface PaymentPageState {
  listingId: string;
  serviceId: string;
  service: PublicService;
  slotStart: string;
  slotEnd: string;
  customerInfo: CustomerInfo;
  depositSettings: DepositSettings;
}

export function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as PaymentPageState | null;

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    if (!state?.listingId) {
      navigate('/book');
      return;
    }
    createSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createSession = async () => {
    if (!state) return;
    try {
      setLoading(true);
      setError(null);

      const createSession = httpsCallable<unknown, {
        clientSecret: string;
        stripeAccountId: string;
        bookingId: string;
        expiresAt: number;
      }>(functions, 'stripeCreateDepositCheckoutSession');

      const idempotencyKey = `${state.listingId}-${state.serviceId}-${state.slotStart}-${state.customerInfo.email}`;

      const result = await createSession({
        listingId: state.listingId,
        serviceId: state.serviceId,
        slotStart: state.slotStart,
        slotEnd: state.slotEnd,
        clientName: `${state.customerInfo.firstName} ${state.customerInfo.lastName}`.trim(),
        clientEmail: state.customerInfo.email,
        clientPhone: state.customerInfo.phone,
        clientNotes: state.customerInfo.notes,
        idempotencyKey,
      });

      setClientSecret(result.data.clientSecret);
      setStripeAccountId(result.data.stripeAccountId);
    } catch (err: any) {
      const code = err?.code ?? '';
      const msg = err?.message ?? '';
      if (code === 'functions/already-exists' || msg.includes('SLOT_UNAVAILABLE')) {
        navigate('/book/date-time', {
          state: { error: 'This time is no longer available. Please choose another.' },
        });
        return;
      }
      console.error('[PaymentPage] createSession failed', err);
      setError('Unable to start payment. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  if (!state) return null;

  const { service, slotStart, depositSettings } = state;
  const slotDate = new Date(slotStart);
  const depositCents = Math.round((service.price_cents * depositSettings.percent) / 100);
  const balanceCents = service.price_cents - depositCents;

  return (
    <div className="min-h-screen bg-site-bg text-site-text">
      <BookingStepper currentStep={4} />

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-2xl font-bold text-site-text">Complete your booking</h1>

        {/* Booking + deposit summary */}
        <div className="bg-site-bg rounded-2xl border border-site-border p-6">
          <div className="mb-4">
            <div className="font-semibold text-site-text">{service.name}</div>
            <div className="text-sm text-site-muted mt-0.5">
              {format(slotDate, 'EEEE, MMMM d, yyyy')} at {format(slotDate, 'h:mm a')}
            </div>
          </div>

          <div className="space-y-2 text-sm border-t border-site-border pt-4">
            <div className="flex justify-between">
              <span className="text-site-muted">Service price</span>
              <span className="font-medium text-site-text">
                {formatPrice(service.price_cents, service.currency)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-site-muted">
                Deposit due today&nbsp;
                <span className="text-site-muted">({depositSettings.percent}%)</span>
              </span>
              <span className="font-semibold" style={{ color: 'var(--site-link)' }}>
                {formatPrice(depositCents, service.currency)}
              </span>
            </div>
            <div className="flex justify-between pt-2 border-t border-site-border">
              <span className="text-site-muted">Balance at appointment</span>
              <span className="font-medium text-site-text">
                {formatPrice(balanceCents, service.currency)}
              </span>
            </div>
          </div>

          <p className="mt-3 text-xs text-site-muted">
            You'll pay the remaining {formatPrice(balanceCents, service.currency)} at your appointment.
          </p>

          {depositSettings.policyText && (
            <details className="mt-4">
              <summary
                className="text-sm font-medium cursor-pointer select-none"
                style={{ color: 'var(--site-link)' }}
              >
                Cancellation policy
              </summary>
              <p className="mt-2 text-sm text-site-muted leading-relaxed">
                {depositSettings.policyText}
              </p>
            </details>
          )}
        </div>

        {/* Error state */}
        {error && (
          <div className="bg-site-danger-soft border border-site-danger/40 rounded-2xl p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-site-danger flex-shrink-0 mt-0.5" />
              <div className="text-sm text-site-danger">{error}</div>
            </div>
            <button
              onClick={createSession}
              className="mt-3 text-sm font-medium underline"
              style={{ color: 'var(--site-link)' }}
            >
              Try again
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !error && (
          <div className="bg-site-bg rounded-2xl border border-site-border p-6 space-y-3">
            <div className="h-4 bg-site-text/10 rounded animate-pulse w-1/3" />
            <div className="h-40 bg-site-text/10 rounded animate-pulse" />
          </div>
        )}

        {/* Stripe Embedded Checkout */}
        {!loading && !error && clientSecret && stripeAccountId && (
          <div className="bg-site-bg rounded-2xl border border-site-border overflow-hidden">
            <DepositCheckout clientSecret={clientSecret} stripeAccountId={stripeAccountId} />
          </div>
        )}
      </div>
    </div>
  );
}
